import React, { Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import GuestRoute from "@/components/auth/GuestRoute";
import AppShell from "@/components/layout/AppShell";

const Login = React.lazy(() => import("@/pages/Login"));
const Dashboard = React.lazy(() => import("@/pages/Dashboard"));
const HoldBills = React.lazy(() => import("@/pages/HoldBills"));
const ProductCategories = React.lazy(() => import("@/pages/masters/ProductCategories"));
const Products = React.lazy(() => import("@/pages/masters/Products"));
const PromotionsMaster = React.lazy(() => import("@/pages/masters/PromotionsMaster"));
const CustomerMaster = React.lazy(() => import("@/pages/masters/CustomerMaster"));
const SupplierMaster = React.lazy(() => import("@/pages/masters/SupplierMaster"));
const ScheduleMaster = React.lazy(() => import("@/pages/accounts/ScheduleMaster"));
const AccountMaster = React.lazy(() => import("@/pages/accounts/AccountMaster"));
const ReceiptVoucher = React.lazy(() => import("@/pages/accounts/ReceiptVoucher"));
const PaymentVoucher = React.lazy(() => import("@/pages/accounts/PaymentVoucher"));
const JournalVoucher = React.lazy(() => import("@/pages/accounts/JournalVoucher"));
const ContraVoucher = React.lazy(() => import("@/pages/accounts/ContraVoucher"));
const DebitNoteVoucher = React.lazy(() => import("@/pages/accounts/DebitNoteVoucher"));
const CreditNoteVoucher = React.lazy(() => import("@/pages/accounts/CreditNoteVoucher"));
const LedgerReport = React.lazy(() => import("@/pages/accounts/LedgerReport"));
const TrialBalanceReport = React.lazy(() => import("@/pages/accounts/TrialBalanceReport"));
const ProfitLossReport = React.lazy(() => import("@/pages/accounts/ProfitLossReport"));
const BalanceSheetReport = React.lazy(() => import("@/pages/accounts/BalanceSheetReport"));
const AccountBooks = React.lazy(() => import("@/pages/accounts/AccountBooks"));
const ReportCenterLayout = React.lazy(() => import("@/components/reports/ReportCenterLayout"));
const ReportCenterHome = React.lazy(() => import("@/pages/reports/ReportCenterHome"));
const SalesReports = React.lazy(() => import("@/pages/reports/SalesReports"));
const CashReports = React.lazy(() => import("@/pages/reports/CashReports"));
const OperationsReports = React.lazy(() => import("@/pages/reports/OperationsReports"));
const StockReports = React.lazy(() => import("@/pages/reports/StockReports"));
const ExpiryReport = React.lazy(() => import("@/pages/reports/ExpiryReport"));
const FinancialReports = React.lazy(() => import("@/pages/reports/FinancialReports"));
const BacklogReports = React.lazy(() => import("@/pages/reports/BacklogReports"));
const UsersPage = React.lazy(() => import("@/pages/admin/Users"));
const RolesPage = React.lazy(() => import("@/pages/admin/Roles"));
const AuditLogPage = React.lazy(() => import("@/pages/admin/AuditLog"));
const CountersPage = React.lazy(() => import("@/pages/admin/Counters"));
const PrintersPage = React.lazy(() => import("@/pages/admin/Printers"));
const CompanySetup = React.lazy(() => import("@/pages/admin/CompanySetup"));
const StoreSetup = React.lazy(() => import("@/pages/admin/StoreSetup"));
const TaxSettings = React.lazy(() => import("@/pages/admin/TaxSettings"));
const GeneralSettings = React.lazy(() => import("@/pages/admin/GeneralSettings"));
const InventoryHubPage = React.lazy(() => import("@/pages/inventory/InventoryHub"));
const StockSummary = React.lazy(() => import("@/pages/inventory/StockSummary"));
const Batches = React.lazy(() => import("@/pages/inventory/Batches"));
const PurchaseOrders = React.lazy(() => import("@/pages/inventory/PurchaseOrders"));
const GoodsReceipts = React.lazy(() => import("@/pages/inventory/GoodsReceipts"));
const Transfers = React.lazy(() => import("@/pages/inventory/Transfers"));
const Adjustments = React.lazy(() => import("@/pages/inventory/Adjustments"));
const Wastage = React.lazy(() => import("@/pages/inventory/Wastage"));
const SupplierReturns = React.lazy(() => import("@/pages/inventory/SupplierReturns"));
const InventoryAlerts = React.lazy(() => import("@/pages/inventory/InventoryAlerts"));
const RetailPOS = React.lazy(() => import("@/pages/pos/RetailPOS"));
const BranchComparisonReport = React.lazy(() => import("@/pages/reports/BranchComparisonReport"));
const SupplierComplianceReport = React.lazy(() => import("@/pages/reports/SupplierComplianceReport"));
const StoreHome = React.lazy(() => import("@/pages/store/StoreHome"));
const StoreCatalog = React.lazy(() => import("@/pages/store/StoreCatalog"));
const StoreCheckout = React.lazy(() => import("@/pages/store/StoreCheckout"));
const OnlineOrders = React.lazy(() => import("@/pages/online/OnlineOrders"));
const OnlineOrderHistory = React.lazy(() => import("@/pages/online/OnlineOrderHistory"));
const OnlineStoreSettings = React.lazy(() => import("@/pages/admin/OnlineStoreSettings"));
const OnlineOrdersReport = React.lazy(() => import("@/pages/reports/OnlineOrdersReport"));
const DiscountReport = React.lazy(() => import("@/pages/reports/DiscountReport"));
const BrandMaster = React.lazy(() => import("@/pages/masters/BrandMaster"));
const ShelfMaster = React.lazy(() => import("@/pages/masters/ShelfMaster"));
const AreaMaster = React.lazy(() => import("@/pages/masters/AreaMaster"));
const UnitCatalog = React.lazy(() => import("@/pages/masters/UnitCatalog"));
const PriceChangeRequests = React.lazy(() => import("@/pages/masters/PriceChangeRequests"));
const BarcodeLabels = React.lazy(() => import("@/pages/masters/BarcodeLabels"));
const PromotionRules = React.lazy(() => import("@/pages/masters/PromotionRules"));
const PurchaseRequests = React.lazy(() => import("@/pages/inventory/PurchaseRequests"));
const PurchaseDebitNotes = React.lazy(() => import("@/pages/inventory/PurchaseDebitNotes"));
const Wrapping = React.lazy(() => import("@/pages/inventory/Wrapping"));
const PdtUploads = React.lazy(() => import("@/pages/inventory/PdtUploads"));
const JobWork = React.lazy(() => import("@/pages/inventory/JobWork"));
const LoyaltySettings = React.lazy(() => import("@/pages/admin/LoyaltySettings"));
const WeighingMachines = React.lazy(() => import("@/pages/admin/WeighingMachines"));
const DenominationMaster = React.lazy(() => import("@/pages/admin/DenominationMaster"));
const ImeiMaster = React.lazy(() => import("@/pages/admin/ImeiMaster"));
const PrepaidCards = React.lazy(() => import("@/pages/admin/PrepaidCards"));
const MisReports = React.lazy(() => import("@/pages/reports/MisReports"));

const routerBasename = import.meta.env.BASE_URL.replace(/\/$/, "") || undefined;

function GuestFallback() {
  return (
    <div className="min-h-screen grid place-items-center bg-brand-surface">
      <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter basename={routerBasename}>
        <Routes>
          <Route
            path="/login"
            element={
              <GuestRoute>
                <Suspense fallback={<GuestFallback />}>
                  <Login />
                </Suspense>
              </GuestRoute>
            }
          />

          <Route
            path="/s/:branchCode"
            element={
              <Suspense fallback={<GuestFallback />}>
                <StoreHome />
              </Suspense>
            }
          />
          <Route
            path="/s/:branchCode/catalog"
            element={
              <Suspense fallback={<GuestFallback />}>
                <StoreCatalog />
              </Suspense>
            }
          />
          <Route
            path="/s/:branchCode/checkout"
            element={
              <Suspense fallback={<GuestFallback />}>
                <StoreCheckout />
              </Suspense>
            }
          />

          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route path="/pos" element={<RetailPOS />} />
            <Route path="/pos/held-bills" element={<HoldBills />} />

            <Route path="/online/orders" element={<OnlineOrders />} />
            <Route path="/online/history" element={<OnlineOrderHistory />} />

            <Route path="/masters/categories" element={<ProductCategories />} />
            <Route path="/masters/products" element={<Products />} />
            <Route path="/masters/brands" element={<BrandMaster />} />
            <Route path="/masters/shelves" element={<ShelfMaster />} />
            <Route path="/masters/areas" element={<AreaMaster />} />
            <Route path="/masters/units" element={<UnitCatalog />} />
            <Route path="/masters/promotions" element={<PromotionsMaster />} />
            <Route path="/masters/promotion-rules" element={<PromotionRules />} />
            <Route path="/masters/promotions/rules" element={<PromotionRules />} />
            <Route path="/masters/price-changes" element={<PriceChangeRequests />} />
            <Route path="/masters/barcode-labels" element={<BarcodeLabels />} />
            <Route path="/masters/customers" element={<CustomerMaster />} />
            <Route path="/masters/suppliers" element={<SupplierMaster />} />

            <Route path="/inventory" element={<InventoryHubPage />} />
            <Route path="/inventory/stock" element={<StockSummary />} />
            <Route path="/inventory/batches" element={<Batches />} />
            <Route path="/inventory/purchase-requests" element={<PurchaseRequests />} />
            <Route path="/inventory/purchase-orders" element={<PurchaseOrders />} />
            <Route path="/inventory/grn" element={<GoodsReceipts />} />
            <Route path="/inventory/purchase-debit-notes" element={<PurchaseDebitNotes />} />
            <Route path="/inventory/transfers" element={<Transfers />} />
            <Route path="/inventory/adjustments" element={<Adjustments />} />
            <Route path="/inventory/wrapping" element={<Wrapping />} />
            <Route path="/inventory/pdt" element={<PdtUploads />} />
            <Route path="/inventory/job-work" element={<JobWork />} />
            <Route path="/inventory/wastage" element={<Wastage />} />
            <Route path="/inventory/returns" element={<SupplierReturns />} />
            <Route path="/inventory/alerts" element={<InventoryAlerts />} />

            <Route path="/accounts/schedules" element={<ScheduleMaster />} />
            <Route path="/accounts/accounts" element={<AccountMaster />} />
            <Route path="/accounts/receipt" element={<ReceiptVoucher />} />
            <Route path="/accounts/payment" element={<PaymentVoucher />} />
            <Route path="/accounts/journal" element={<JournalVoucher />} />
            <Route path="/accounts/contra" element={<ContraVoucher />} />
            <Route path="/accounts/debit-note" element={<DebitNoteVoucher />} />
            <Route path="/accounts/credit-note" element={<CreditNoteVoucher />} />
            <Route path="/accounts/ledger" element={<LedgerReport />} />
            <Route path="/accounts/trial-balance" element={<TrialBalanceReport />} />
            <Route path="/accounts/profit-loss" element={<ProfitLossReport />} />
            <Route path="/accounts/balance-sheet" element={<BalanceSheetReport />} />
            <Route path="/accounts/cash-book" element={<AccountBooks />} />
            <Route path="/accounts/bank-book" element={<AccountBooks />} />
            <Route path="/accounts/day-book" element={<AccountBooks />} />

            <Route path="/reports" element={<ReportCenterLayout />}>
              <Route index element={<ReportCenterHome />} />
              <Route path="daily-sales" element={<SalesReports />} />
              <Route path="counter-sales" element={<SalesReports />} />
              <Route path="item-sales" element={<SalesReports />} />
              <Route path="tax" element={<SalesReports />} />
              <Route path="cash-collection" element={<CashReports />} />
              <Route path="z-report" element={<CashReports />} />
              <Route path="x-report" element={<CashReports />} />
              <Route path="sales-day-book" element={<CashReports />} />
              <Route path="customer-sales" element={<OperationsReports />} />
              <Route path="delivery-area-sales" element={<OperationsReports />} />
              <Route path="condensed-stock" element={<StockReports />} />
              <Route path="opening-stock" element={<StockReports />} />
              <Route path="physical-stock" element={<StockReports />} />
              <Route path="batch-expiry" element={<ExpiryReport />} />
              <Route path="financial-statements" element={<FinancialReports />} />
              <Route path="customer-age-wise" element={<FinancialReports />} />
              <Route path="supplier-age-wise" element={<FinancialReports />} />
              <Route path="customer-ledger" element={<FinancialReports />} />
              <Route path="supplier-ledger" element={<FinancialReports />} />
              <Route path="category-sales" element={<BacklogReports />} />
              <Route path="top-items" element={<BacklogReports />} />
              <Route path="supplier-summary" element={<BacklogReports />} />
              <Route path="order-summary" element={<BacklogReports />} />
              <Route path="branch-comparison" element={<BranchComparisonReport />} />
              <Route path="supplier-compliance" element={<SupplierComplianceReport />} />
              <Route path="online-orders" element={<OnlineOrdersReport />} />
              <Route path="discounts" element={<DiscountReport />} />
              <Route path="mis" element={<MisReports />} />
              <Route path="ledger" element={<LedgerReport />} />
              <Route path="trial-balance" element={<TrialBalanceReport />} />
              <Route path="profit-loss" element={<ProfitLossReport />} />
              <Route path="balance-sheet" element={<BalanceSheetReport />} />
              <Route path="cash-book" element={<AccountBooks />} />
              <Route path="bank-book" element={<AccountBooks />} />
              <Route path="day-book" element={<AccountBooks />} />
            </Route>

            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/admin/roles" element={<RolesPage />} />
            <Route path="/admin/audit-log" element={<AuditLogPage />} />
            <Route path="/admin/counters" element={<CountersPage />} />
            <Route path="/admin/printers" element={<PrintersPage />} />
            <Route path="/admin/loyalty" element={<LoyaltySettings />} />
            <Route path="/admin/denominations" element={<DenominationMaster />} />
            <Route path="/admin/weighing-machines" element={<WeighingMachines />} />
            <Route path="/admin/imei" element={<ImeiMaster />} />
            <Route path="/admin/prepaid-cards" element={<PrepaidCards />} />
            <Route path="/admin/company" element={<CompanySetup />} />
            <Route path="/admin/store" element={<StoreSetup />} />
            <Route path="/admin/tax" element={<TaxSettings />} />
            <Route path="/admin/general" element={<GeneralSettings />} />
            <Route path="/admin/online-store" element={<OnlineStoreSettings />} />
            <Route path="/reports/online-orders" element={<OnlineOrdersReport />} />
            <Route path="/reports/mis" element={<MisReports />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="bottom-right" />
    </div>
  );
}

export default App;
